// Sticky Keys service — lets the user press modifier keys (Shift,
// Ctrl, Alt, Meta) one at a time instead of holding them down. Once
// a modifier is "locked", the next non-modifier key is dispatched as
// if the modifier had been held.
//
// A small pill overlay shows which modifiers are currently active.

const MODIFIERS = ['Shift', 'Control', 'Alt', 'Meta'];
const SHORT = { Shift: '⇧', Control: 'Ctrl', Alt: 'Alt', Meta: '⌘' };

let started = false;
let active = new Set();
let indicator = null;

function ensureIndicator() {
  if (indicator) return;
  indicator = document.createElement('div');
  indicator.className = 'a11y-sticky-indicator';
  indicator.setAttribute('aria-live', 'polite');
  indicator.setAttribute('aria-label', 'Active modifier keys');
  indicator.style.cssText = [
    'position:fixed',
    'bottom:16px',
    'left:50%',
    'transform:translateX(-50%)',
    'background:#111827',
    'color:#ffffff',
    'padding:8px 14px',
    'border-radius:9999px',
    'font-family:Poppins,sans-serif',
    'font-size:12px',
    'font-weight:700',
    'letter-spacing:0.05em',
    'box-shadow:0 8px 20px rgba(0,0,0,0.25)',
    'z-index:9998',
    'display:none',
    'gap:6px',
    'align-items:center'
  ].join(';');
  document.body.appendChild(indicator);
}

function render() {
  if (!indicator) return;
  if (!active.size) {
    indicator.style.display = 'none';
    indicator.innerHTML = '';
    return;
  }
  indicator.style.display = 'inline-flex';
  indicator.innerHTML =
    '<span style="opacity:0.7;font-weight:500;">Sticky:</span>' +
    Array.from(active)
      .map(
        (m) =>
          '<span style="background:#047873;padding:2px 8px;border-radius:6px;">' +
          SHORT[m] +
          '</span>'
      )
      .join('');
}

function onKeyDown(e) {
  // If this is a modifier key press, toggle it
  if (MODIFIERS.includes(e.key)) {
    if (active.has(e.key)) active.delete(e.key);
    else active.add(e.key);
    render();
    // Let the event propagate normally too, so legitimate combo usage
    // still works for users who want to do both.
    return;
  }
  // Non-modifier key with sticky modifiers active → synthesise a
  // combo KeyboardEvent and clear the sticky set.
  if (active.size > 0 && !e.repeat) {
    // Don't interfere if the user is actually holding the modifier
    const alreadyHeld =
      (active.has('Shift') && e.shiftKey) ||
      (active.has('Control') && e.ctrlKey) ||
      (active.has('Alt') && e.altKey) ||
      (active.has('Meta') && e.metaKey);
    if (!alreadyHeld) {
      const init = {
        key: e.key,
        code: e.code,
        bubbles: true,
        cancelable: true,
        shiftKey: active.has('Shift') || e.shiftKey,
        ctrlKey: active.has('Control') || e.ctrlKey,
        altKey: active.has('Alt') || e.altKey,
        metaKey: active.has('Meta') || e.metaKey
      };
      // Re-dispatch as if the modifier were held. Cancel the original
      // event so we don't double-fire.
      e.preventDefault();
      e.stopImmediatePropagation();
      const target = e.target || document.activeElement || document.body;
      target.dispatchEvent(new KeyboardEvent('keydown', init));
      target.dispatchEvent(new KeyboardEvent('keyup', init));
    }
    active.clear();
    render();
  }
}

export function startStickyKeys() {
  if (started) return;
  ensureIndicator();
  document.addEventListener('keydown', onKeyDown, true);
  started = true;
}

export function stopStickyKeys() {
  if (!started) return;
  document.removeEventListener('keydown', onKeyDown, true);
  active.clear();
  render();
  if (indicator && indicator.parentNode)
    indicator.parentNode.removeChild(indicator);
  indicator = null;
  started = false;
}
